import { FC, HTMLAttributes, useMemo, useState, useEffect } from "react";
import { useQuery } from "@apollo/client/react/hooks/useQuery";
import { Query } from "@onmo/graphql/graphql";
import clsx from "classnames";
import {
  meAAAGQL,
  featuredCardsAaaGql,
  meNotificationsGQL,
} from "../../graphql";
import { Console as ConsoleLoading } from "../../components/common/PageLoading/Console";
import { useStyles } from "./Console.style";
import { useAppSelector } from "../../components/hooks";
import { selectIsAuthenticated } from "@onmo/auth";
import ConsoleMobileContent from "../../components/common/ConsoleMobileContent/ConsoleMobileContent";
import ConsoleDesktopContent from "../../components/common/ConsoleDesktopContent/ConsoleDesktopContent";
import { selectIsDesktopView } from "../../store/slices/appState";
import {
  FeatureDesktopGroup,
  FeatureMobileGroup,
} from "../../constants/console";
import { WelcomePopup } from "../../components/common/WelcomePopup";
import { isFounderNotifications } from "../../models/user";
import { useProfileSwitchFade } from "../../hooks/useProfileSwitchFade";

const Console: FC<HTMLAttributes<HTMLDivElement>> = () => {
  const classes = useStyles();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isDesktop = useAppSelector(selectIsDesktopView);
  const switchFade = useProfileSwitchFade();
  const { data: dataMe, loading: loadingMe } = useQuery<{
    me: Query["me"];
  }>(meAAAGQL, {
    skip: !isAuthenticated,
  });
  const { loading: loadingFeaturedCards } = useQuery<Query>(
    featuredCardsAaaGql,
    {
      variables: {
        input: { group: isDesktop ? FeatureDesktopGroup : FeatureMobileGroup },
      },
    },
  );
  const { data: dataNotifications, refetch } = useQuery<{ me: Query["me"] }>(
    meNotificationsGQL,
    {
      variables: { product: "aaa" },
      skip: !isAuthenticated,
      fetchPolicy: "cache-and-network",
    },
  );
  const { isBonus, isFounder } = isFounderNotifications(
    dataNotifications?.me?.notifications || [],
  );
  const [isOpenWelcome, setIsOpenWelcome] = useState(!!isBonus || !!isFounder);
  const loading = loadingMe || loadingFeaturedCards;

  const getTimePlayed = useMemo(() => {
    if (!dataMe?.me?.totalTimePlayed) return 0;
    const hour = dataMe?.me?.totalTimePlayed / 3600;
    if (hour <= 1) {
      return Math.floor(hour * 10) / 10;
    }
    return Math.ceil(hour - 0.5);
  }, [dataMe?.me?.totalTimePlayed]);

  const onCloseWelcome = async () => {
    setIsOpenWelcome(false);
    await refetch();
  };

  useEffect(() => {
    setIsOpenWelcome(!!isBonus || !!isFounder);
  }, [isBonus, isFounder]);

  return (
    <div className={clsx(classes.root, switchFade)}>
      {loading ? (
        <ConsoleLoading />
      ) : isDesktop ? (
        <ConsoleDesktopContent
          totalTimePlayed={getTimePlayed}
          totalPlayedGames={dataMe?.me?.totalPlayedGames || 0}
        />
      ) : (
        <ConsoleMobileContent
          user={dataMe?.me}
          totalTimePlayed={getTimePlayed}
          totalPlayedGames={dataMe?.me?.totalPlayedGames || 0}
        />
      )}
      <WelcomePopup isOpen={isOpenWelcome} onClose={onCloseWelcome} />
    </div>
  );
};

export default Console;
